import { useState, useEffect, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { UserAvatar } from "@/modern/components/user-avatar";
import type { BoardUser } from "@/react/hooks/entityHooks/useEntityOwnership";

interface OverflowUsersPopoverProps {
  assignedUsers: Array<BoardUser>;
}

export const OverflowUsersPopover = ({ assignedUsers }: OverflowUsersPopoverProps) => {
  const [open, setOpen] = useState(false);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Set open with delay
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
    }
    openTimeoutRef.current = setTimeout(() => {
      setOpen(true);
      openTimeoutRef.current = null;
    }, 300);
  };

  const handleMouseLeave = () => {
    // Clear any pending open timeout
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    // Set close with delay
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 200);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="h-8 min-w-8 px-2 rounded-full bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors ring-2 ring-background"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">+{assignedUsers.length}</span>
        </div>
      </PopoverTrigger>
      <PopoverContent side="top" align="center" className="w-64 p-0" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="max-h-64 overflow-auto p-2 space-y-1">
          {assignedUsers.map((user) => {
            return (
              <div key={user.id} className="p-2 hover:bg-accent transition-colors rounded-md">
                <UserAvatar user={user} size="small" showName={true} />
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};